import type { Messages } from '../catalogue.en'

/**
 * The doctor dashboard, including the board.
 *
 * Composed into the public catalogue by `catalogue.he.ts`. Nothing imports this
 * module directly: consumers ask the catalogue, so how the messages are stored
 * stays this layer's business.
 */
export const dashboard: Partial<Messages> = {
  'dashboard.title': 'לוח בקרה',

  /* The board */

  'dashboard.boardSections': 'מקטעי לוח הבקרה',
  'dashboard.boardSection': '{title}: {count} {unit}',
  'dashboard.boardSectionSeries': '{title}. {series}. {caption}',
  'dashboard.boardSeriesFigure': '{label}: {count}',

  'dashboard.attentionTitle': 'דורש התייחסות',
  'dashboard.attentionUnit': 'פריטים',
  'dashboard.attentionLink': 'הצגת הכול',
  'dashboard.attentionFooter': 'הצגת כל הפריטים',

  'dashboard.dayTitle': 'לוח הזמנים שלי להיום',
  'dashboard.dayUnit': 'תורים',
  'dashboard.dayLink': 'הצגת לוח הזמנים',
  'dashboard.dayShown': 'מוצגים {shown} מתוך {total} תורים',
  'dashboard.dayFilterAll': 'כל המצבים',
  'dashboard.dayLoading': 'לוח הזמנים להיום נטען',
  'dashboard.dayUnavailable': 'הגישה ללוח הזמנים אינה זמינה',
  'dashboard.clinicDayTitle': 'לוח הזמנים של המרפאה להיום',
  'dashboard.dayEmpty': 'אין תורים היום',
  'dashboard.dayEmptyDetail': 'אין תורים ביום המרפאה שנקבע על ידי השרת.',
  'dashboard.dayFilterEmpty': 'אין תורים במצב הזה',
  'dashboard.dayLoadFailed': 'לא ניתן היה לטעון את לוח הזמנים להיום.',
  'dashboard.dayStale': 'לא ניתן היה לרענן את לוח הזמנים.',
  'dashboard.dayLastConfirmed': 'אושר לאחרונה בשעה {time}',
  'dashboard.dayRetry': 'ניסיון חוזר',

  'dashboard.myTasksTitle': 'המשימות שלי',
  'dashboard.myTasksUnit': 'ממתינות',
  'dashboard.myTasksLink': 'פתיחת המשימות',
  'dashboard.myTasksFooter': 'הצגת משימות שהושלמו',

  'dashboard.followUpsTitle': 'מעקבים פתוחים',
  'dashboard.followUpsUnit': 'ממתינים',
  'dashboard.followUpsLink': 'סקירת המעקבים',
  'dashboard.followUpsFooter': 'הצגת כל המעקבים',

  'dashboard.eventsTitle': 'לוח הזמנים של המטופלים שלי',
  'dashboard.eventsUnit': 'אירועים',
  'dashboard.eventsLink': 'הצגת אירועי המטופלים',
  'dashboard.eventsFooter': 'הצגת לוח הזמנים המלא',

  /* Panel chrome */

  'dashboard.chooseDay': 'בחירת יום',
  'dashboard.filter': 'סינון',
  'dashboard.filters': 'מסננים',
  'dashboard.sortDueDate': 'תאריך יעד',
  'dashboard.scopeAllFollowUps': 'כל המעקבים',
  'dashboard.viewList': 'רשימה',
  'dashboard.viewTimeline': 'ציר זמן',
  'dashboard.columnFollowUp': 'מעקב',
  'dashboard.columnDue': 'תאריך יעד',
  'dashboard.columnEvent': 'אירוע',
  'dashboard.columnLocation': 'מיקום',
  'dashboard.taskScopeAll': 'הכול ({count})',
  'dashboard.taskScopeClinical': 'קליני ({count})',
  'dashboard.taskScopeAdmin': 'מנהלי ({count})',
  'dashboard.taskGroupOverdue': 'באיחור ({count})',
  'dashboard.taskGroupDueToday': 'להיום ({count})',
  'dashboard.taskGroupUpcoming': 'קרובות ({count})',

  /* Row vocabulary */

  'dashboard.personMeta': 'בן/בת {age} · {sex}',
  'dashboard.minutes': '{count} דק׳',
  'dashboard.room': 'חדר {number}',
  'dashboard.consultRoom': 'חדר ייעוץ {number}',
  'dashboard.ward': 'מחלקה {number}',
  'dashboard.phlebotomy': 'נטילת דם',
  'dashboard.televisit': 'ביקור מרחוק',
  'dashboard.dueAt': 'יעד {when}',
  'dashboard.yesterday': 'אתמול',
  'dashboard.tomorrow': 'מחר',
  'dashboard.inDays': 'בעוד {count} ימים',
  'dashboard.daysAgo': 'לפני {count} ימים',

  /* Status vocabulary */

  'dashboard.stateNew': 'חדש',
  'dashboard.stateHigh': 'גבוה',
  'dashboard.stateReview': 'לעיון',
  'dashboard.stateUnsigned': 'לא חתום',
  'dashboard.stateOverdue': 'באיחור',
  'dashboard.stateResponse': 'ממתין לתשובה',
  'dashboard.stateUpcoming': 'קרוב',
  'dashboard.stateScheduled': 'מתוכנן',
  'dashboard.stateInProgress': 'בתהליך',
  'dashboard.stateCompleted': 'הושלם',
  'dashboard.stateAwaiting': 'בהמתנה',
  'dashboard.stateAwaitingResult': 'ממתין לתוצאה',
  'dashboard.stateAwaitingPatient': 'ממתין למטופל',
  'dashboard.stateDueSoon': 'מתקרב ליעד',
  'dashboard.stateDueToday': 'להיום',

  /* Needs attention */

  'dashboard.attentionNewResult': 'תוצאת מעבדה חדשה',
  'dashboard.attentionNewResultDetail': 'ספירת דם מלאה',
  'dashboard.attentionOutOfRange': 'תוצאה מחוץ לטווח הייחוס',
  'dashboard.attentionOutOfRangeDetail': 'אשלגן',
  'dashboard.attentionNewDocument': 'מסמך חדש לעיון',
  'dashboard.attentionNewDocumentDetail': 'דוח הדמיה',
  'dashboard.attentionUnsignedNote': 'רשומה ללא חתימה',
  'dashboard.attentionUnsignedNoteDetail': 'רשומת התקדמות',
  'dashboard.attentionOverdueFollowUp': 'מעקב באיחור',
  'dashboard.attentionOverdueFollowUpDetail': 'המעקב היה אמור להתבצע לפני {count} ימים',
  'dashboard.attentionMessage': 'מטופל ממתין לתשובתך',
  'dashboard.attentionMessageDetail': 'הודעה בפורטל',

  /* My tasks */


  /* Open follow-ups */

  'dashboard.followUpResult': 'ממתין לתוצאת מעבדה',
  'dashboard.followUpResultDetail': 'ויטמין B12',
  'dashboard.followUpImagingDisk': 'המטופל ישלח את דיסק ההדמיה',
  'dashboard.followUpImagingDiskDetail': 'סריקת עמוד שדרה מותני',
  'dashboard.followUpRecheck': 'ביקורת חוזרת בעוד שלושה חודשים',
  'dashboard.followUpRecheckDetail': 'מעקב סוכרת',
  'dashboard.followUpConsult': 'יש לקבוע ייעוץ',
  'dashboard.followUpConsultDetail': 'דיון באפשרויות הטיפול',
  'dashboard.followUpBiopsy': 'עיון בדוח הביופסיה',
  'dashboard.followUpBiopsyDetail': 'דוח פתולוגיה בהמתנה',
  'dashboard.followUpReply': 'הודעת מטופל ממתינה לתשובה',
  'dashboard.followUpReplyDetail': 'הודעה מהמטופל בפורטל',

  /* My patients' schedule */

  'dashboard.eventImaging': 'סריקת מוח',
  'dashboard.eventImagingCategory': 'הדמיה',
  'dashboard.eventConsult': 'ייעוץ מומחה',
  'dashboard.eventConsultCategory': 'ייעוץ',
  'dashboard.eventBloodTest': 'בדיקת דם',
  'dashboard.eventBloodTestCategory': 'פתולוגיה',
  'dashboard.eventAdmission': 'אשפוז לניתוח',
  'dashboard.eventAdmissionCategory': 'אורתופדיה',
  'dashboard.eventTeleconsultation': 'ייעוץ מרחוק',
  'dashboard.eventTeleconsultationCategory': 'מעקב',
  'dashboard.eventDischarge': 'ביקור שחרור',
  'dashboard.eventDischargeCategory': 'רפואה פנימית',
  'dashboard.placeImaging': 'מכון הדמיה',
  'dashboard.placeClinic': 'מרפאה',
  'dashboard.placeLaboratory': 'מעבדה',
  'dashboard.placeHospital': 'בית חולים',
  'dashboard.placeVirtual': 'ביקור מקוון',

  /* The context column */

  'dashboard.nextAppointment': 'התור הבא',
  'dashboard.railTitle': 'סיכום',
  'dashboard.dayOverview': 'סקירת היום',
  'dashboard.overviewAppointments': 'תורים',
  'dashboard.overviewInPerson': 'פרונטלי',
  'dashboard.overviewTelevisits': 'ביקורים מרחוק',
  'dashboard.overviewCompleted': 'הושלמו',
  'dashboard.overviewCheckedIn': 'נרשמו בקבלה',
  'dashboard.overviewWindow': '{from} – {to}',
  'dashboard.todaysPatients': 'המטופלים של היום',
  'dashboard.railMore': 'ועוד {count}',
  'dashboard.taskSummary': 'סיכום משימות',
  'dashboard.summaryTotal': 'סך הכול',
  'dashboard.summaryTotalRow': 'סך הכול',
  'dashboard.summaryWaitingOnOthers': 'בהמתנה לאחרים',
  'dashboard.followUpSummary': 'סיכום מעקבים',
  'dashboard.clinicTimeNote': 'כל השעות מוצגות באזור הזמן של המרפאה',

  'dashboard.quickActions': 'פעולות מהירות',
  'dashboard.actionNewLead': 'ליד חדש',
  'dashboard.actionNewLeadDetail': 'התחלת קליטת מטופל',
  'dashboard.actionNewTask': 'משימה חדשה',
  'dashboard.actionNewTaskDetail': 'יצירת משימה',
  'dashboard.actionSendMessage': 'שליחת הודעה',
  'dashboard.actionSendMessageDetail': 'כתיבה למטופל או לרופא',
  'dashboard.actionUploadDocument': 'העלאת מסמך',
  'dashboard.actionUploadDocumentDetail': 'הוספת קובץ',
  'dashboard.actionCallPatient': 'התקשרות למטופל',
  'dashboard.actionCallPatientDetail': 'תיעוד שיחה',
  'dashboard.actionCreateFollowUp': 'יצירת מעקב',
  'dashboard.actionMessagePatient': 'הודעה למטופל',
  'dashboard.actionSendReminder': 'שליחת תזכורת',
}
